import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '../ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { Switch } from '../ui/switch';
import { Skeleton } from '../ui/skeleton';
import { useToast } from '../ui/use-toast';
import { UserPlus, X, Search, Pencil, Check, Mail } from 'lucide-react';
import { staffService } from '../../services/staff';
import type { Staff, User } from '../../services/staff';

/**
 * The roles somebody holds here without a row here — through the circuit this
 * is a stage of, or the gym it is held at — keyed on the user's slug, each role
 * naming the place it is held at. The server reads the union of those and the
 * row here, so the row can only ever add to them: the picker shows them as
 * already on, and the row wears them next to its own.
 */
export type InheritedRoles = Record<string, Record<string, string>>;

interface GymStaffManagerProps {
  /** Location id whose staff is being managed. */
  gymId: string;
  /** Slug of the signed-in user, so we never offer to remove yourself. */
  currentUserSlug?: string;
  /**
   * What kind of place this is. The roles worth granting differ: a circuit has
   * nobody setting or writing copy, and a stage is the one place a judge is a
   * real job. Defaults to a gym's, which is every existing caller.
   */
  locationType?: 'gym' | 'crag' | 'trophy' | 'competition';
  /** See `InheritedRoles`. Only a competition has any. */
  inheritedRoles?: InheritedRoles;
}

/**
 * The roles offered per kind of place. Every one of them exists on `Staff`; this
 * only decides which are worth putting in front of somebody — offering a crag a
 * judge, or a circuit a route setter, is noise in a checklist that has to be
 * read quickly.
 */
const ROLES_BY_LOCATION = {
  gym: ['admin', 'editor', 'routesetter', 'member_manager'],
  crag: ['admin', 'editor', 'routesetter'],
  // A circuit is run and its entrants are managed. The climbing happens at the
  // stages, and its admins reach every one of them anyway.
  trophy: ['admin', 'member_manager'],
  // A stage's own team: whoever runs it, the judges scoring it, the setters who
  // built it and the desk signing people in.
  competition: ['admin', 'judge', 'routesetter', 'member_manager'],
} as const;

/** Nothing inherited: the picker and the badges of every place but a stage. */
const NO_ROLES: Record<string, string> = {};

/**
 * The roles as a row of toggles. The same control picks the roles of a new
 * invitation and rewrites those of a row already there, so a role reads the
 * same in both places.
 *
 * A role in `locked` is held through the circuit or the gym: it is shown on
 * and cannot be turned off here, because turning it off here would change
 * nothing — the place it is held at is where it is taken away.
 */
function RolePicker({
  roles,
  selected,
  locked = NO_ROLES,
  onToggle,
  disabled,
}: {
  roles: readonly string[];
  selected: string[];
  locked?: Record<string, string>;
  onToggle: (role: string) => void;
  disabled?: boolean;
}) {
  const { t } = useTranslation();
  return (
    <div className="flex flex-wrap gap-2">
      {roles.map((role) => {
        const from = locked[role];
        const on = !!from || selected.includes(role);
        return (
          <Button
            key={role}
            type="button"
            variant={on ? 'default' : 'outline'}
            size="sm"
            onClick={() => onToggle(role)}
            disabled={disabled || !!from}
            aria-pressed={on}
            title={from ? t('gymSettings.inheritedRole', { place: from }) : undefined}
          >
            {t(`gymSettings.${role}Role`)}
          </Button>
        );
      })}
    </div>
  );
}

/**
 * The roles a row grants, and after them the ones the person holds here
 * through somewhere else — outlined, and saying where — so the row reads as
 * what the server will actually allow.
 */
function RoleBadges({ own, inherited }: { own: string[]; inherited: Record<string, string> }) {
  const { t } = useTranslation();
  return (
    <>
      {own.map((role) => (
        <Badge key={role} variant="secondary">
          {t(`gymSettings.${role}Role`)}
        </Badge>
      ))}
      {Object.entries(inherited)
        .filter(([role]) => !own.includes(role))
        .map(([role, place]) => (
          <Badge
            key={role}
            variant="outline"
            title={t('gymSettings.inheritedRole', { place })}
            data-testid="inherited-role"
          >
            {t(`gymSettings.${role}Role`)}
          </Badge>
        ))}
    </>
  );
}

/**
 * How a row is named in the URL. Somebody invited by address who has not signed
 * up yet has no username and so no slug; the server takes the row's id instead.
 */
function staffKey(staff: Staff): string {
  return staff.user.slug ?? staff.id;
}

/**
 * The second line under a name: the username, or — for an invitation still
 * waiting on somebody to open their account — the address it went to.
 */
function StaffIdentity({ staff }: { staff: Staff }) {
  const { t } = useTranslation();
  if (staff.user.invitation_pending) {
    return (
      <div className="text-sm text-muted-foreground" data-testid="awaiting-sign-up">
        {t('gymSettings.awaitingSignUp', { email: staff.user.email })}
      </div>
    );
  }
  return <div className="text-sm text-muted-foreground">@{staff.user.username}</div>;
}

/** The name a row goes by — the address, until the person has given one. */
function displayName(staff: Staff): string {
  return staff.user.full_name?.trim() || staff.user.email;
}

/** The row whose roles are being rewritten, with the roles as picked so far. */
interface RoleEdit {
  staffId: string;
  roles: string[];
}

/**
 * Staff management card: list current staff with their roles/status, invite new
 * staff by searching users, rewrite a member's roles, remove staff and toggle
 * the coach flag. Fetches its own data so it can be dropped into both the gym
 * settings page and the gym-admin Staff page.
 */
export function GymStaffManager({
  gymId,
  currentUserSlug,
  locationType = 'gym',
  inheritedRoles,
}: GymStaffManagerProps) {
  const { t } = useTranslation();
  const { toast } = useToast();
  const roles = ROLES_BY_LOCATION[locationType] ?? ROLES_BY_LOCATION.gym;
  const inheritedBy = (slug: string) => inheritedRoles?.[slug] ?? NO_ROLES;

  const [staffList, setStaffList] = useState<Staff[]>([]);
  const [loading, setLoading] = useState(true);

  // Add staff form state
  const [showAddForm, setShowAddForm] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<User[]>([]);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [selectedRoles, setSelectedRoles] = useState<string[]>([]);
  const [isCreating, setIsCreating] = useState(false);

  // Inviting by address instead of picking from the search: a judge or a
  // setter from the hosting club often has no account yet, and the server
  // opens one for them to finish (or finds the one the address belongs to).
  const [inviteByEmail, setInviteByEmail] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteName, setInviteName] = useState('');
  const [inviteSurname, setInviteSurname] = useState('');

  // One row at a time has its roles open for editing — an invitation not yet
  // answered as much as a confirmed member: the roles are what the invitation
  // offers, and getting them wrong should not mean withdrawing it.
  const [roleEdit, setRoleEdit] = useState<RoleEdit | null>(null);
  const [savingRoles, setSavingRoles] = useState(false);

  useEffect(() => {
    let isMounted = true;
    setLoading(true);
    staffService
      .getLocationStaff(gymId)
      .then((staff) => {
        if (isMounted) setStaffList(staff);
      })
      .catch((error) => {
        console.error('Failed to fetch staff:', error);
      })
      .finally(() => {
        if (isMounted) setLoading(false);
      });
    return () => {
      isMounted = false;
    };
  }, [gymId]);

  // Search users (debounced)
  useEffect(() => {
    if (searchQuery.length < 2) {
      setSearchResults([]);
      return;
    }

    const searchTimeout = setTimeout(async () => {
      try {
        const results = await staffService.searchUsers(searchQuery);
        setSearchResults(results);
      } catch (error) {
        console.error('Search failed:', error);
      }
    }, 300);

    return () => clearTimeout(searchTimeout);
  }, [searchQuery]);

  const handleRemoveStaff = async (staff: Staff) => {
    const confirmed = window.confirm(
      t('gymSettings.confirmRemoveDescription', { name: displayName(staff) })
    );
    if (!confirmed) return;

    try {
      // The API names a staff member by their user's slug, not their username.
      await staffService.removeStaff(gymId, staffKey(staff));
      setStaffList((prev) => prev.filter((s) => s.id !== staff.id));
      toast({
        title: t('gymSettings.staffRemoved'),
        description: t('gymSettings.staffRemovedDescription', { name: displayName(staff) }),
      });
    } catch (error) {
      console.error('Failed to remove staff:', error);
      toast({
        title: t('gymSettings.removeStaffError'),
        description: error instanceof Error ? error.message : t('errors.unexpectedError'),
        variant: 'destructive',
      });
    }
  };

  const handleToggleCoach = async (staff: Staff, coach: boolean) => {
    // Optimistic update so the switch responds immediately.
    setStaffList((prev) => prev.map((s) => (s.id === staff.id ? { ...s, user: { ...s.user, coach } } : s)));
    try {
      await staffService.setStaffCoach(gymId, staff.user.slug, coach);
      toast({
        title: t('gymSettings.coach.updated'),
        description: coach
          ? t('gymSettings.coach.enabledDescription', { name: staff.user.full_name })
          : t('gymSettings.coach.disabledDescription', { name: staff.user.full_name }),
      });
    } catch (error) {
      // Roll back on failure.
      setStaffList((prev) => prev.map((s) => (s.id === staff.id ? { ...s, user: { ...s.user, coach: !coach } } : s)));
      console.error('Failed to update coach role:', error);
      toast({
        title: t('gymSettings.coach.error'),
        description: error instanceof Error ? error.message : t('errors.unexpectedError'),
        variant: 'destructive',
      });
    }
  };

  // Somebody already on the staff through the circuit or the gym gets no
  // invitation: the server confirms the row on the spot and the roles are in
  // force, so the button and the toast say so rather than promising a letter.
  const selectedInherited = selectedUser ? inheritedBy(selectedUser.slug) : NO_ROLES;
  const selectedAlreadyStaff = Object.keys(selectedInherited).length > 0;

  const inviteEmailValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(inviteEmail.trim());
  const canAdd = selectedRoles.length > 0 && (inviteByEmail ? inviteEmailValid : !!selectedUser);

  const resetAddForm = () => {
    setShowAddForm(false);
    setSelectedUser(null);
    setSelectedRoles([]);
    setSearchQuery('');
    setSearchResults([]);
    setInviteByEmail(false);
    setInviteEmail('');
    setInviteName('');
    setInviteSurname('');
  };

  const handleAddStaff = async () => {
    if (!canAdd) return;

    setIsCreating(true);
    try {
      const newStaff = await staffService.createStaff(
        gymId,
        inviteByEmail
          ? {
              email: inviteEmail.trim(),
              name: inviteName.trim() || undefined,
              surname: inviteSurname.trim() || undefined,
              staff_roles: selectedRoles,
            }
          : { user_slug: selectedUser!.slug, staff_roles: selectedRoles }
      );

      setStaffList((prev) => [...prev, newStaff]);
      resetAddForm();

      const name = displayName(newStaff);
      toast(
        newStaff.status === 'confirmed'
          ? {
              title: t('gymSettings.rolesAdded'),
              description: t('gymSettings.rolesAddedDescription', { name }),
            }
          : newStaff.user.invitation_pending
            ? {
                title: t('gymSettings.staffInvited'),
                description: t('gymSettings.staffInvitedByEmailDescription', { email: newStaff.user.email }),
              }
            : {
                title: t('gymSettings.staffInvited'),
                description: t('gymSettings.staffInvitedDescription', { name }),
              }
      );
    } catch (error) {
      console.error('Failed to add staff:', error);
      toast({
        title: t('gymSettings.addStaffError'),
        description: error instanceof Error ? error.message : t('errors.unexpectedError'),
        variant: 'destructive',
      });
    } finally {
      setIsCreating(false);
    }
  };

  const toggleRole = (role: string) => {
    setSelectedRoles((prev) => (prev.includes(role) ? prev.filter((r) => r !== role) : [...prev, role]));
  };

  const toggleEditedRole = (role: string) => {
    setRoleEdit((edit) =>
      edit
        ? {
            ...edit,
            roles: edit.roles.includes(role) ? edit.roles.filter((r) => r !== role) : [...edit.roles, role],
          }
        : edit
    );
  };

  const handleSaveRoles = async (staff: Staff) => {
    if (!roleEdit || roleEdit.roles.length === 0) return;

    setSavingRoles(true);
    try {
      const updated = await staffService.setStaffRoles(gymId, staffKey(staff), roleEdit.roles);
      setStaffList((prev) => prev.map((s) => (s.id === staff.id ? { ...s, staff_roles: updated.staff_roles } : s)));
      setRoleEdit(null);
      toast({
        title: t('gymSettings.rolesUpdated'),
        description: t('gymSettings.rolesUpdatedDescription', { name: displayName(staff) }),
      });
    } catch (error) {
      console.error('Failed to update staff roles:', error);
      toast({
        title: t('gymSettings.rolesUpdateError'),
        description: error instanceof Error ? error.message : t('errors.unexpectedError'),
        variant: 'destructive',
      });
    } finally {
      setSavingRoles(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center justify-between">
          {t('gymSettings.staffManagement')}
          <Button onClick={() => setShowAddForm(!showAddForm)} size="sm">
            <UserPlus className="h-4 w-4 mr-2" />
            {t('gymSettings.addStaff')}
          </Button>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Add Staff Form */}
        {showAddForm && (
          <Card className="border-dashed">
            <CardContent className="pt-6 space-y-4">
              {inviteByEmail ? (
                <div className="space-y-3" data-testid="invite-by-email">
                  <div className="space-y-2">
                    <Label htmlFor="inviteEmail">{t('gymSettings.inviteEmail')}</Label>
                    <div className="relative">
                      <Mail className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                      <Input
                        id="inviteEmail"
                        type="email"
                        autoComplete="off"
                        placeholder={t('gymSettings.inviteEmailPlaceholder')}
                        value={inviteEmail}
                        onChange={(e) => setInviteEmail(e.target.value)}
                        className="pl-9"
                      />
                    </div>
                  </div>
                  <div className="grid gap-3 sm:grid-cols-2">
                    <div className="space-y-2">
                      <Label htmlFor="inviteName">{t('gymSettings.inviteName')}</Label>
                      <Input id="inviteName" value={inviteName} onChange={(e) => setInviteName(e.target.value)} />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="inviteSurname">{t('gymSettings.inviteSurname')}</Label>
                      <Input
                        id="inviteSurname"
                        value={inviteSurname}
                        onChange={(e) => setInviteSurname(e.target.value)}
                      />
                    </div>
                  </div>
                  <p className="text-sm text-muted-foreground">{t('gymSettings.inviteByEmailHint')}</p>
                  <Button type="button" variant="link" className="h-auto p-0" onClick={() => setInviteByEmail(false)}>
                    {t('gymSettings.searchInstead')}
                  </Button>
                </div>
              ) : (
                <div className="space-y-2">
                  <Label htmlFor="userSearch">{t('gymSettings.searchUser')}</Label>
                  <div className="relative">
                    <Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                    <Input
                      id="userSearch"
                      placeholder={t('gymSettings.searchUserPlaceholder')}
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="pl-9"
                    />
                  </div>

                  {/* Search Results */}
                  {searchResults.length > 0 && (
                    <div className="border rounded-md max-h-40 overflow-y-auto">
                      {searchResults.map((searchUser) => (
                        <div
                          key={searchUser.id}
                          className="p-3 hover:bg-muted cursor-pointer border-b last:border-b-0"
                          onClick={() => {
                            setSelectedUser(searchUser);
                            setSearchQuery(searchUser.full_name);
                            setSearchResults([]);
                          }}
                        >
                          <div className="font-medium">{searchUser.full_name}</div>
                          <div className="text-sm text-muted-foreground">@{searchUser.username}</div>
                        </div>
                      ))}
                    </div>
                  )}

                  <Button
                    type="button"
                    variant="link"
                    className="h-auto p-0"
                    onClick={() => {
                      setInviteByEmail(true);
                      setSelectedUser(null);
                      setSearchResults([]);
                    }}
                  >
                    {t('gymSettings.inviteByEmail')}
                  </Button>
                </div>
              )}

              {/* Selected User */}
              {!inviteByEmail && selectedUser && (
                <div className="p-3 bg-muted rounded-md">
                  <div className="font-medium">{selectedUser.full_name}</div>
                  <div className="text-sm text-muted-foreground">@{selectedUser.username}</div>
                  {selectedAlreadyStaff && (
                    <p className="mt-2 text-sm text-muted-foreground" data-testid="already-staff-hint">
                      {t('gymSettings.alreadyStaffThrough', {
                        places: [...new Set(Object.values(selectedInherited))].join(', '),
                      })}
                    </p>
                  )}
                </div>
              )}

              {/* Role Selection */}
              <div className="space-y-2">
                <Label>{t('gymSettings.selectRoles')}</Label>
                <RolePicker roles={roles} selected={selectedRoles} locked={selectedInherited} onToggle={toggleRole} />
              </div>

              {/* Actions */}
              <div className="flex gap-2">
                <Button onClick={handleAddStaff} disabled={!canAdd || isCreating}>
                  {isCreating
                    ? t('gymSettings.inviting')
                    : selectedAlreadyStaff
                      ? t('gymSettings.addRoles')
                      : t('gymSettings.sendInvitation')}
                </Button>
                <Button variant="outline" onClick={resetAddForm}>
                  {t('common.cancel')}
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Staff List */}
        <div className="space-y-3">
          {loading ? (
            <>
              <Skeleton className="h-20 w-full" />
              <Skeleton className="h-20 w-full" />
            </>
          ) : staffList.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">{t('gymSettings.noStaff')}</div>
          ) : (
            staffList.map((staff) => {
              const editing = roleEdit?.staffId === staff.id ? roleEdit : null;
              // Your own row is left alone: taking your own admin role away, or
              // removing yourself, would lock you out of the very page you are on.
              const isSelf = staff.user.slug === currentUserSlug;
              const inherited = inheritedBy(staff.user.slug);

              return (
                <div key={staff.id} className="flex items-center justify-between p-4 border rounded-lg" data-testid="staff-row">
                  <div className="flex-1">
                    <div className="font-medium">{displayName(staff)}</div>
                    <StaffIdentity staff={staff} />
                    {editing ? (
                      <div className="mt-2 space-y-2">
                        <RolePicker
                          roles={roles}
                          selected={editing.roles}
                          locked={inherited}
                          onToggle={toggleEditedRole}
                          disabled={savingRoles}
                        />
                        <div className="flex gap-2">
                          <Button
                            size="sm"
                            onClick={() => handleSaveRoles(staff)}
                            disabled={editing.roles.length === 0 || savingRoles}
                          >
                            <Check className="h-4 w-4 mr-2" />
                            {savingRoles ? t('common.saving') : t('common.save')}
                          </Button>
                          <Button size="sm" variant="outline" onClick={() => setRoleEdit(null)} disabled={savingRoles}>
                            {t('common.cancel')}
                          </Button>
                        </div>
                      </div>
                    ) : (
                      <div className="flex flex-wrap gap-2 mt-2">
                        <RoleBadges own={staff.staff_roles} inherited={inherited} />
                        <Badge
                          className={
                            staff.status === 'confirmed'
                              ? 'bg-green-100 text-green-800'
                              : staff.status === 'pending'
                                ? 'bg-yellow-100 text-yellow-800'
                                : 'bg-red-100 text-red-800'
                          }
                          variant={
                            staff.status === 'confirmed'
                              ? 'default'
                              : staff.status === 'pending'
                                ? 'secondary'
                                : 'destructive'
                          }
                        >
                          {t(`gymSettings.status.${staff.status}`)}
                        </Badge>
                      </div>
                    )}
                  </div>

                  <div className="flex items-center gap-3">
                    {/* Coach toggle: marks this user as an allenatore who can be
                        invited to build training plans. Only for confirmed staff,
                        and only at a gym — coaching is something a gym offers its
                        members; a crag, a circuit or a stage has no athletes to
                        train. */}
                    {staff.status === 'confirmed' && locationType === 'gym' && (
                      <div className="flex items-center gap-2">
                        <Switch
                          id={`coach-${staff.id}`}
                          checked={staff.user.coach ?? false}
                          onCheckedChange={(checked) => handleToggleCoach(staff, checked)}
                        />
                        <Label htmlFor={`coach-${staff.id}`} className="cursor-pointer text-sm">
                          {t('gymSettings.coach.label')}
                        </Label>
                      </div>
                    )}

                    {!isSelf && !editing && (
                      <Button
                        variant="ghost"
                        size="sm"
                        // The picker edits what this row grants on its own; a
                        // role it repeats from the circuit or the gym is locked
                        // there anyway, and saving leaves it to that place.
                        onClick={() =>
                          setRoleEdit({
                            staffId: staff.id,
                            roles: staff.staff_roles.filter((role) => !inherited[role]),
                          })
                        }
                        title={t('gymSettings.editRoles')}
                        aria-label={t('gymSettings.editRoles')}
                      >
                        <Pencil className="h-4 w-4" />
                      </Button>
                    )}

                    {!isSelf && (
                      <Button
                        variant="ghost"
                        size="sm"
                        className="text-destructive"
                        onClick={() => handleRemoveStaff(staff)}
                        title={t('gymSettings.removeStaff')}
                        aria-label={t('gymSettings.removeStaff')}
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    )}
                  </div>
                </div>
              );
            })
          )}
        </div>
      </CardContent>
    </Card>
  );
}
